import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';
import NotificationContainer from '../Notification/NotificationContainer';
import '../../assets/css/admin-modern.css';

const AdminSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [saving, setSaving] = useState(false);

  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getSettings();
      const settingsList = Array.isArray(response) ? response : (response?.data || []);

      if (settingsList.length === 0) {
        try {
          await initializeDefaultSettings();
          const newResponse = await adminApi.getSettings();
          const newSettingsList = Array.isArray(newResponse) ? newResponse : (newResponse?.data || []);
          setSettings(newSettingsList);
        } catch (initError) {
          console.error('Erreur lors de l\'initialisation:', initError);
          setSettings([]);
        }
        return;
      }

      // Normaliser les valeurs boolean pour le select
      const normalizedSettings = settingsList.map(setting => {
        if (setting.type === 'boolean') {
          // Normaliser la valeur boolean pour le select (doit être "true" ou "false")
          let normalizedValue = setting.value;
          if (normalizedValue === true || normalizedValue === '1' || normalizedValue === 1 || normalizedValue === 'true') {
            normalizedValue = 'true';
          } else if (normalizedValue === false || normalizedValue === '0' || normalizedValue === 0 || normalizedValue === 'false') {
            normalizedValue = 'false';
          }
          return { ...setting, value: normalizedValue };
        }
        return setting;
      });

      setSettings(normalizedSettings);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      if (!error.message || !error.message.includes('settings')) {
        addNotification('Erreur lors du chargement des paramètres', 'error');
      }
      setSettings([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultSettings = async () => {
    try {
      const defaultSettings = {
        'delivery_fee': { value: '5000', type: 'number', group: 'delivery', description: 'Frais de livraison par défaut' },
        'delivery_fee_per_km': { value: '500', type: 'number', group: 'delivery', description: 'Frais de livraison par kilomètre' },
        'free_delivery_threshold': { value: '50000', type: 'number', group: 'delivery', description: 'Montant minimum pour la livraison gratuite' },
        'tax_rate': { value: '18', type: 'number', group: 'tax', description: 'Taux de taxe en pourcentage' },
        'tax_enabled': { value: 'true', type: 'boolean', group: 'tax', description: 'Activer les taxes' },
        'currency': { value: 'FCFA', type: 'string', group: 'general', description: 'Devise' },
        'currency_symbol': { value: 'FCFA', type: 'string', group: 'general', description: 'Symbole de la devise' },
        'min_order_amount': { value: '5000', type: 'number', group: 'general', description: 'Montant minimum de commande' },
      };

      await adminApi.updateSettings(defaultSettings);
    } catch (error) {
      console.error('Erreur lors de l\'initialisation des paramètres:', error);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => prev.map(setting =>
      setting.key === key
        ? { ...setting, value: value.toString() }
        : setting
    ));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const settingsObject = {};
      settings.forEach(setting => {
        let value = setting.value;

        // Normaliser les valeurs boolean avant sauvegarde
        if (setting.type === 'boolean') {
          // S'assurer que la valeur est "true" ou "false" (string)
          value = (value === true || value === 'true' || value === '1' || value === 1) ? 'true' : 'false';
        }

        settingsObject[setting.key] = {
          value: value,
          type: setting.type || 'string',
          group: setting.group || 'general',
          description: setting.description || null
        };
      });

      await adminApi.updateSettings(settingsObject);
      addNotification('Paramètres sauvegardés avec succès', 'success');

      // Recharger les paramètres après sauvegarde pour s'assurer de la synchronisation
      await loadSettings();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      addNotification(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getSettingsByGroup = (group) => {
    return settings.filter(s => s.group === group);
  };

  const formatPrice = (value) => {
    if (!value && value !== 0) return '0';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0';
    return numValue.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-500">Chargement des paramètres...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <NotificationContainer notifications={notifications} removeNotification={removeNotification} />

      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Paramètres</span>
      </div>

      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Paramètres Système</h1>
            <p className="page-subtitle">Configurer les paramètres du marché agricole</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
            style={{ minWidth: '180px' }}
          >
            {saving ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sauvegarde...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Sauvegarder
              </>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Frais de livraison */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-icon bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <div>
              <h3 className="settings-card-title">Frais de Livraison</h3>
              <p className="settings-card-description">Configurez les tarifs et conditions de livraison</p>
            </div>
          </div>
          <div className="settings-card-body">
            <div className="settings-grid">
              {getSettingsByGroup('delivery').map((setting) => (
                <div key={setting.key} className="settings-field">
                  <label className="settings-label">
                    {setting.description || setting.key.replace(/_/g, ' ')}
                    {setting.key.includes('fee') && <span className="settings-unit">(FCFA)</span>}
                  </label>
                  {setting.type === 'boolean' ? (
                    <select
                      value={setting.value === true || setting.value === 'true' || setting.value === '1' || setting.value === 1 ? 'true' : 'false'}
                      onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                      className="settings-input"
                    >
                      <option value="true">Oui</option>
                      <option value="false">Non</option>
                    </select>
                  ) : (
                    <div className="settings-input-wrapper">
                      <input
                        type={setting.type === 'number' ? 'number' : 'text'}
                        value={setting.value || ''}
                        onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                        className="settings-input"
                        step={setting.type === 'number' ? '1' : undefined}
                        min={setting.type === 'number' ? '0' : undefined}
                        placeholder="0"
                      />
                      {setting.key.includes('fee') && setting.value && (
                        <span className="settings-input-suffix">FCFA</span>
                      )}
                    </div>
                  )}
                  {setting.key.includes('fee') && setting.value && (
                    <p className="settings-help">Format: {formatPrice(setting.value)} FCFA</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Taxes */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-icon bg-purple-100">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="settings-card-title">Taxes</h3>
              <p className="settings-card-description">Configurez les taux et l'activation des taxes</p>
            </div>
          </div>
          <div className="settings-card-body">
            <div className="settings-grid">
              {getSettingsByGroup('tax').map((setting) => (
                <div key={setting.key} className="settings-field">
                  <label className="settings-label">
                    {setting.description || setting.key.replace(/_/g, ' ')}
                    {setting.key.includes('rate') && <span className="settings-unit">(%)</span>}
                  </label>
                  {setting.type === 'boolean' ? (
                    <select
                      value={setting.value}
                      onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                      className="settings-input"
                    >
                      <option value="true">Oui</option>
                      <option value="false">Non</option>
                    </select>
                  ) : (
                    <div className="settings-input-wrapper">
                      <input
                        type={setting.type === 'number' ? 'number' : 'text'}
                        value={setting.value || ''}
                        onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                        className="settings-input"
                        step={setting.type === 'number' ? '0.1' : undefined}
                        min={setting.type === 'number' ? '0' : undefined}
                        max={setting.key.includes('rate') ? '100' : undefined}
                      />
                      {setting.key.includes('rate') && setting.value && (
                        <span className="settings-input-suffix">%</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Paramètres généraux */}
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-icon bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h3 className="settings-card-title">Paramètres Généraux</h3>
              <p className="settings-card-description">Configurez les paramètres généraux de l'application</p>
            </div>
          </div>
          <div className="settings-card-body">
            <div className="settings-grid">
              {getSettingsByGroup('general').map((setting) => (
                <div key={setting.key} className="settings-field">
                  <label className="settings-label">
                    {setting.description || setting.key.replace(/_/g, ' ')}
                    {setting.key.includes('amount') && <span className="settings-unit">(FCFA)</span>}
                  </label>
                  <div className="settings-input-wrapper">
                    <input
                      type={setting.type === 'number' ? 'number' : 'text'}
                      value={setting.value || ''}
                      onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                      className="settings-input"
                      step={setting.type === 'number' ? '1' : undefined}
                      min={setting.type === 'number' ? '0' : undefined}
                      placeholder="0"
                    />
                    {setting.key.includes('amount') && setting.value && (
                      <span className="settings-input-suffix">FCFA</span>
                    )}
                  </div>
                  {setting.key.includes('amount') && setting.value && (
                    <p className="settings-help">Format: {formatPrice(setting.value)} FCFA</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
